// What every export format starts from: the run's title, its markdown and a
// stable JSON shape.
import { ArticleGenerationRow, ArticleSeo } from '../../../types/articleGeneration';
import { buildMarkdown } from '../markdown';

export interface ExportJson {
  format: 'hopkai-article/1';
  exportedAt: string;
  id: string;
  mode: ArticleGenerationRow['mode'];
  title: string;
  language: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  headings: string[];
  briefs: { heading: string; brief: string }[];
  sections: { index: number; heading: string; brief: string; body: string }[];
  markdown: string;
  seo: ArticleSeo | null;
  createdAt: string;
  status: ArticleGenerationRow['status'];
}

export function exportTitle(run: ArticleGenerationRow): string {
  return (run.structure?.h1 || run.briefs?.h1 || run.inputs.primaryKeyword || '').trim();
}

/** The search metadata as the leading lines of an export (labels as the user reads them). */
export function seoMarkdown(seo: ArticleSeo | null | undefined): string {
  if (!seo) return '';
  const lines = [`**Title:** ${seo.title}`];
  if (seo.metaDescription) lines.push(`**Meta description:** ${seo.metaDescription}`);
  lines.push(`**H1:** ${seo.h1}`);
  return lines.join('\n\n');
}

/** The artifact as markdown: the SEO lines, then the article or the plan as H1 + H2 (+ brief). */
export function exportMarkdown(run: ArticleGenerationRow): string {
  const body = artifactMarkdown(run);
  const seo = seoMarkdown(run.seo);
  return seo ? `${seo}\n\n${body}` : body;
}

function artifactMarkdown(run: ArticleGenerationRow): string {
  const h1 = exportTitle(run);
  if (run.mode === 'full_article') {
    return buildMarkdown(h1, run.sections ?? []);
  }
  const parts = [`# ${h1}`];
  if (run.mode === 'headings_briefs') {
    for (const s of run.briefs?.sections ?? []) {
      parts.push(`## ${s.heading}`);
      if (s.brief.trim()) parts.push(s.brief.trim());
    }
  } else {
    for (const heading of run.structure?.headings ?? []) parts.push(`## ${heading}`);
  }
  return parts.join('\n\n');
}

export function exportJson(run: ArticleGenerationRow, now: Date = new Date()): ExportJson {
  return {
    format: 'hopkai-article/1',
    exportedAt: now.toISOString(),
    id: run.id,
    mode: run.mode,
    title: exportTitle(run),
    language: run.inputs.language,
    primaryKeyword: run.inputs.primaryKeyword,
    secondaryKeywords: run.inputs.secondaryKeywords ?? [],
    headings: run.structure?.headings ?? [],
    briefs: (run.briefs?.sections ?? []).map((s) => ({ heading: s.heading, brief: s.brief })),
    sections: [...(run.sections ?? [])]
      .sort((a, b) => a.index - b.index)
      .map((s) => ({ index: s.index, heading: s.heading, brief: s.brief, body: s.body })),
    markdown: exportMarkdown(run),
    seo: run.seo ?? null,
    createdAt: run.created_at,
    status: run.status,
  };
}

const SLUG_MAX = 60;

export function slugify(text: string): string {
  const ascii = text
    .toLowerCase()
    .replace(/[äå]/g, 'a')
    .replace(/ö/g, 'o')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return ascii.slice(0, SLUG_MAX).replace(/-+$/g, '');
}

/** `<slug>-<yyyy-mm-dd>.<ext>`, with "article" when the title is empty. */
export function exportFilename(run: ArticleGenerationRow, ext: string, now: Date = new Date()): string {
  const slug = slugify(exportTitle(run)) || 'article';
  const date = now.toISOString().slice(0, 10);
  return `${slug}-${date}.${ext.replace(/^\./, '')}`;
}
